import { useEffect, useState } from "react";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import { useRecoilState, useRecoilValue } from "recoil";
import MinusButton from "../../../../components/MinusButton";
import PlusButton from "../../../../components/PlusButton";
import SelectHookFormSingle from "../../../../components/SelectHookFormSingle";
import { toast } from "../../../../components/toastNotification";
import {
  changeToUpdate,
  fileImageForDelete,
  imageDisplays,
  officeImageAttachment,
  rowImageForDelete,
} from "../atoms/atoms";
import { useGetOfficeImageLabel } from "../hooks/useGetOfficeImageLable";
import { useGetOfficeImageCategory } from "../hooks/usetGetOfficeImageCategory";
import { permissionsSelector } from "../../../Login/selectors/permissionSelector";
import { IOfficeImageSection } from "../types";
import OfficeImageSectionAddForm from "./OfficeImageSectionAddForm";
import { OFFICEFIELD_PERMISSIONS } from "../permission";

interface OfficeImageDetailsProps {
  fileImagesList?: Array<IOfficeImageSection>;
  isAddState: boolean;
}
const defaultfileImagesList = [
  {
    parentRow: true,
    tableName: "fileImageLists",
  },
] as Array<IOfficeImageSection>;

const OfficeImageSectionForm: React.FC<OfficeImageDetailsProps> = ({
  fileImagesList = defaultfileImagesList,
  isAddState,
}) => {
  //Data fetching
  const { data: imageCategory, isLoading: categoryLoading } =
    useGetOfficeImageCategory();
  const { data: imageLabel, isLoading: labelLoading } =
    useGetOfficeImageLabel();

  //Image category options
  const categories = imageCategory
    ?.filter((x) => x.isActive === true)
    .map((y) => {
      return {
        id: y.id,
        value: y.id,
        label: y.imageCategoryName,
      };
    });

  //Image label options
  const labels = imageLabel
    ?.filter((x) => x.isActive === true)
    ?.map((y) => {
      return {
        id: y.id,
        value: y.id,
        label: y.imageLabelName,
      };
    });

  const formMethods = useFormContext();
  const { control, watch, setValue } = formMethods;

  // Attachment
  const [attachments, setAttachments] = useRecoilState<(File | null)[]>(
    officeImageAttachment
  );
  const [attachmentDisplay, setAttachmentDisplay] = useState<any>(
    watch("fileNames") ? watch("fileNames") : []
  );
  const [, setImageDisplay] = useRecoilState(imageDisplays);

  const [imageForDelete, setImageForDelete] =
    useRecoilState<any>(fileImageForDelete);
  const [rowForDelete, setRowForDelete] =
    useRecoilState<any>(rowImageForDelete);

  //Data fetching
  const [imageCategoryName, setImageCategoryName] = useState<any>();
  const [imageLabelName, setImageLabelName] = useState<any>();

  //Permission
  const userPermissions = useRecoilValue(permissionsSelector);
  const uploadPermission =
    OFFICEFIELD_PERMISSIONS.fieldpermission_office_officeimage_upload;

  //#region add/minus of fields
  const { fields, append, remove, replace } = useFieldArray({
    control,
    name:
      fileImagesList.length > 0 && fileImagesList[0].tableName
        ? fileImagesList[0].tableName
        : "",
  });

  const tableArray = fileImagesList.length > 0 && watch("fileImageLists");

  const controlledFields =
    tableArray &&
    fields.map((field, index) => ({
      ...field,
      ...tableArray[index],
    }));

  //SETTING DEFAULT TABLE VALUES
  useEffect(() => {
    if (fileImagesList) {
      replace(fileImagesList);
    }
  }, [fileImagesList]);
  //#endregion

  //For removing image
  const removeFile = (attachmentName, fileName) => {
    setAttachmentDisplay(
      attachmentDisplay.filter((x) => x.fileImageName !== fileName)
    );
    setAttachments(attachments.filter((x) => x?.name !== attachmentName));
    setImageForDelete([
      ...imageForDelete,
      fileImagesList.filter((x) => x.fileImageName === fileName)[0],
    ]);
  };

  //For removing fields w/ image
  const removeRowFile = (attachmentName, fileName) => {
    setAttachmentDisplay(
      attachmentDisplay.filter((x) => x.fileImageName !== fileName)
    );
    setAttachments(attachments.filter((x) => x?.name !== attachmentName));
    setImageForDelete([
      ...imageForDelete,
      fileImagesList.filter((x) => x.fileImageName === fileName)[0],
    ]);
    setRowForDelete([
      ...rowForDelete,
      fileImagesList.filter((x) => x.fileImageName === fileName)[0],
    ]);
  };

  useEffect(() => {
    setImageDisplay(attachmentDisplay);
  }, [attachmentDisplay, fields]);

  const [onChangeToUpdate, setOnchangeToUpdate] = useState<any>([]);
  const [onchangeToUpdateList, setOnchangeToUpdateList] =
    useRecoilState(changeToUpdate);

  useEffect(() => {
    const interval = setInterval(() => {
      if (!isAddState) {
        const cFields = JSON.parse(JSON.stringify(watch("fileImageLists")));
        const cFieldRemove = watch("fileImageLists");
        // To update
        const onchangeUpdate = cFields.filter(
          (x, index) =>
            (x?.imageCategoryId !== fileImagesList[index]?.imageCategoryId ||
              x?.imageLabelId !== fileImagesList[index]?.imageLabelId) &&
            x?.fileImageName === fileImagesList[index]?.fileImageName
        );

        if (onchangeUpdate?.length > 0) {
          const newChanges = [...onChangeToUpdate, ...onchangeUpdate];
          const uniqueUpdateSet = new Set(newChanges);
          const uniqueUpdateArray = Array.from(uniqueUpdateSet);

          setOnchangeToUpdateList(uniqueUpdateArray);
        }
        //For removing both value in fields and image
        if (cFieldRemove?.length === 1) {
          if (
            (cFieldRemove[0]?.imageCategoryId === null ||
              cFieldRemove[0]?.imageLabelId === null) &&
            imageForDelete?.length > 0
          ) {
            setRowForDelete([...imageForDelete]);
          }
        }
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [watch("fileImageLists"), controlledFields, rowForDelete]);

  return (
    <div className="w-full px-2">
      <div className="w-full bg-white p-2  overflow-x-auto box-shadow rounded-lg border-t">
        <div className="text-quinary font-bold font-sans py-2 px-4">
          Office Image Section
        </div>
        {controlledFields &&
          controlledFields.map((field, index) => {
            // Get the office code using watch
            const officeCode = watch("officeCode");

            // Remove the office code from the fileImageName
            const fileImageName = field.fileImageName?.replace(
              `${officeCode}-`,
              ""
            );
            return (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 mx-4 pb-2 lg:lg:gap-12 gap-2  gap-2">
                  <div key={field.id}>
                    <div className="flex flex-row items-center gap-2">
                      {userPermissions.includes(uploadPermission) &&
                        watch("officeCode") && (
                          <div>
                            {index > 0 ? (
                              <MinusButton
                                className="mr-5"
                                type="button"
                                onClick={() => {
                                  removeRowFile(
                                    field.fileName,
                                    field.fileImageName
                                  );
                                  setValue(
                                    `${fileImagesList[0].tableName}[${index}].fileImageName`,
                                    null
                                  );
                                  remove(index);
                                }}
                              />
                            ) : (
                              <PlusButton
                                className="mt-5"
                                type="button"
                                onClick={(e) => {
                                  //Checker of null category and label
                                  const isNull = controlledFields.filter(
                                    (x) =>
                                      x.imageCategoryId === null ||
                                      x.imageLabelId === null
                                  );

                                  if (attachmentDisplay?.length > 0) {
                                    if (
                                      attachmentDisplay?.length ===
                                        controlledFields?.length &&
                                      isNull?.length === 0
                                    ) {
                                      append({
                                        tableName: fileImagesList[0]?.tableName,
                                        parentRow: false,
                                      });
                                    } else {
                                      toast.fire(
                                        "Failed to add new row!",
                                        "Please fill image category, image label field and choose image.",
                                        "warning"
                                      );
                                    }
                                  } else {
                                    toast.fire(
                                      "Failed to add new row!",
                                      "Please fill image category, image label field and choose image.",
                                      "warning"
                                    );
                                  }
                                }}
                              />
                            )}
                          </div>
                        )}

                      <Controller
                        name={`${fileImagesList[0].tableName}[${index}].imageCategoryId`}
                        control={formMethods.control}
                        rules={{ required: false }}
                        render={({ field }) => (
                          <SelectHookFormSingle
                            label={
                              index < 1 && fileImagesList[0].parentRow === true
                                ? "Image Category"
                                : undefined
                            }
                            field={field}
                            onChange={(e) => {
                              field.onChange(e ? e.value : null);
                              setImageCategoryName(e ? e.label : null);
                              setValue(
                                `${fileImagesList[0].tableName}[${index}].imageCategoryName`,
                                e?.label
                              );
                            }}
                            value={field.value ? field.value : null}
                            options={categories}
                            isLoading={categoryLoading}
                            required={false}
                            secondDiv={
                              index < 1 && fileImagesList[0].parentRow === true
                                ? "grid grid-cols-1"
                                : ""
                            }
                            isDisabled={
                              !userPermissions.includes(uploadPermission) ||
                              !watch("officeCode")
                            }
                          />
                        )}
                      />
                      <Controller
                        name={`${fileImagesList[0].tableName}[${index}].imageLabelId`}
                        control={formMethods.control}
                        rules={{ required: false }}
                        render={({ field }) => (
                          <SelectHookFormSingle
                            label={
                              index < 1 && fileImagesList[0].parentRow === true
                                ? "Image Label"
                                : undefined
                            }
                            field={field}
                            onChange={(e) => {
                              field.onChange(e ? e.value : null);
                              setImageLabelName(e ? e.label : null);
                              setValue(
                                `${fileImagesList[0].tableName}[${index}].imageLabelName`,
                                e?.label
                              );
                            }}
                            value={field.value ? field.value : undefined}
                            isLoading={labelLoading}
                            options={labels}
                            required={false}
                            secondDiv={
                              index < 1 && fileImagesList[0].parentRow === true
                                ? "grid grid-cols-1"
                                : ""
                            }
                            isDisabled={
                              !watch("officeCode") ||
                              !userPermissions.includes(uploadPermission)
                            }
                          />
                        )}
                      />
                    </div>
                  </div>
                  <div className="space-y-1">
                    {index < 1 && fileImagesList[0].parentRow === true ? (
                      <label className="flex flex-row items-center text-xs mb-1">
                        <b className="text-italic text-red-500 ">Note:&nbsp;</b>{" "}
                        Kindly choose an image to upload
                      </label>
                    ) : undefined}
                    <div className="text-xs">
                      <div className="">
                        {field.fileImageName && (
                          <div className="flex bg-gray-50 rounded-md justify-between px-2 py-1">
                            <div className="font-semibold text-left text-primary pr-2">
                              {fileImageName.split(".").slice(0, -1).join(".")}
                            </div>
                            {userPermissions.includes(uploadPermission) &&
                              watch("officeCode") && (
                                <div className="flex items-center">
                                  <MinusButton
                                    onClick={() => {
                                      removeFile(
                                        field.fileName,
                                        field.fileImageName
                                      );
                                      setValue(
                                        `${fileImagesList[0].tableName}[${index}].fileImageName`,
                                        null
                                      );
                                      setImageCategoryName(
                                        field.imageCategoryName
                                      );
                                      setImageLabelName(field.imageLabelName);
                                    }}
                                  />
                                </div>
                              )}
                          </div>
                        )}
                      </div>
                      <div className="flex justify-center py-2">
                        {!watch(
                          `${fileImagesList[0].tableName}[${index}].fileImageName`
                        ) && (
                          <label
                            className={
                              !watch("officeCode") ||
                              !userPermissions.includes(uploadPermission) ||
                              watch(
                                `${fileImagesList[0].tableName}[${index}].fileImageName`
                              ) ||
                              !watch(
                                `${fileImagesList[0].tableName}[${index}].imageCategoryId`
                              ) ||
                              !watch(
                                `${fileImagesList[0].tableName}[${index}].imageLabelId`
                              )
                                ? "opacity-50 text-center bg-primary text-white py-1 px-3 border font-medium rounded-full transition duration-500"
                                : "text-center bg-primary text-white py-1 px-3 border font-medium rounded-full cursor-pointer transition duration-500 hover:bg-white hover:text-primary hover:border-primary"
                            }
                          >
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              disabled={
                                !watch("officeCode") ||
                                !userPermissions.includes(uploadPermission) ||
                                watch(
                                  `${fileImagesList[0].tableName}[${index}].fileImageName`
                                ) ||
                                !watch(
                                  `${fileImagesList[0].tableName}[${index}].imageCategoryId`
                                ) ||
                                !watch(
                                  `${fileImagesList[0].tableName}[${index}].imageLabelId`
                                )
                              }
                              onChange={(e) => {
                                if (
                                  e.target.files &&
                                  e.target.files.length > 0
                                ) {
                                  var reader = new FileReader();
                                  reader.readAsDataURL(e.target.files[0]);
                                  reader.onload = () => {
                                    var rowIndex = fileImagesList.findIndex(
                                      (obj) =>
                                        obj.fileImageName ===
                                        e?.target?.files?.item(0)?.name
                                    );
                                    if (
                                      !e?.target?.files
                                        ?.item(0)
                                        ?.type.includes("image")
                                    ) {
                                      toast.fire(
                                        "Attachment failed",
                                        "Invalid file image.",
                                        "error"
                                      );
                                    } else if (rowIndex < 0) {
                                      setAttachmentDisplay([
                                        ...attachmentDisplay,
                                        {
                                          fileName:
                                            e?.target?.files?.item(0)?.name,
                                          fileImageName:
                                            watch("officeCode") +
                                            "-" +
                                            imageCategoryName +
                                            "-" +
                                            imageLabelName +
                                            "." +
                                            e?.target?.files
                                              ?.item(0)
                                              ?.name.split(".")
                                              .pop(),
                                          attachments:
                                            e?.target?.files?.item(0),
                                        },
                                      ]);
                                      setValue(
                                        `${fileImagesList[0].tableName}[${index}].fileImageName`,
                                        watch("officeCode") +
                                          "-" +
                                          imageCategoryName +
                                          "-" +
                                          imageLabelName +
                                          "." +
                                          e?.target?.files
                                            ?.item(0)
                                            ?.name.split(".")
                                            .pop()
                                      );
                                      setValue(
                                        `${fileImagesList[0].tableName}[${index}].attachments`,
                                        e?.target?.files?.item(0)
                                      );

                                      const newFile = e.target.files
                                        ? e.target.files.item(0)
                                        : null;
                                      formMethods.setValue(
                                        "fileNames",
                                        newFile
                                      );
                                      if (attachments[0]) {
                                        setAttachments((old) => [
                                          ...old,
                                          newFile,
                                        ]);
                                      } else {
                                        setAttachments([newFile]);
                                      }
                                      e.target.value = "";
                                    } else {
                                      toast.fire(
                                        "Attachment failed",
                                        "File already exists",
                                        "error"
                                      );
                                      e.target.value = "";
                                    }
                                  };
                                }
                              }}
                            />
                            Choose Image
                          </label>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </>
            );
          })}
        {isAddState && <OfficeImageSectionAddForm />}
      </div>
    </div>
  );
};
export default OfficeImageSectionForm;
